import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Tag,
  Typography,
  App,
  Form,
  Select,
  Switch,
} from 'antd';
import { withDevModalFilePath, ListActionsDropdown, PageHeader, PageContainer } from '../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/users/UserListPage.tsx');
import {
  SearchOutlined,
  ReloadOutlined,
  KeyOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { adminPath } from '../../../lib/adminPaths';
import type { MenuProps } from 'antd';
import { getMutationPendingState } from '../../../utils/trpcMutation';

const { Text } = Typography;
const { Option } = Select;

type UserRole = 'admin' | 'manager' | 'user' | 'viewer';

interface User {
  id: string;
  email: string;
  name: string;
  isActive: boolean;
  companyId: string | null;
  companyName?: string | null;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
  deletedAt: string | null;
  deletionReason?: string | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeUser = (value: unknown): User | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  const role = value.role;
  const normalizedRole: UserRole =
    role === 'admin' || role === 'manager' || role === 'user' || role === 'viewer'
      ? role
      : 'user';

  return {
    id: value.id,
    email: typeof value.email === 'string' ? value.email : '',
    name: typeof value.name === 'string' ? value.name : '',
    isActive: typeof value.isActive === 'boolean' ? value.isActive : false,
    companyId: typeof value.companyId === 'string' ? value.companyId : null,
    companyName: typeof value.companyName === 'string' ? value.companyName : null,
    role: normalizedRole,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : '',
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : '',
    lastLoginAt: typeof value.lastLoginAt === 'string' ? value.lastLoginAt : null,
    deletedAt: typeof value.deletedAt === 'string' ? value.deletedAt : null,
    deletionReason: typeof value.deletionReason === 'string' ? value.deletionReason : null,
  };
};

const roleLabels: Record<UserRole, string> = {
  admin: '管理者',
  manager: 'マネージャー',
  user: 'ユーザー',
  viewer: '閲覧者',
};

const roleColors: Record<UserRole, string> = {
  admin: 'red',
  manager: 'orange',
  user: 'blue',
  viewer: 'default',
};

interface CreateUserFormValues {
  companyId: string;
  name: string;
  email: string;
  password: string;
  isActive?: boolean;
}

interface UpdateUserFormValues {
  name: string;
  email: string;
  isActive?: boolean;
  role?: UserRole;
}

interface ResetPasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

export function UserListPage() {
  const { message } = App.useApp();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showDeleted, setShowDeleted] = useState(false);

  const {
    data: companyList,
    isLoading: companyLoading,
    error: companyListError,
  } = trpc.company.list.useQuery({ page: 1, limit: 100, sortBy: 'name', sortOrder: 'asc' });

  const companyOptions = useMemo(() => {
    const companies = (companyList?.data ?? []) as Array<{
      id?: string;
      name?: string;
      isActive?: boolean | null;
    }>;
    return companies
      .filter(
        (company): company is { id: string; name: string; isActive?: boolean | null } =>
          typeof company.id === 'string' &&
          typeof company.name === 'string' &&
          company.isActive !== false,
      )
      .map((company) => ({
        value: company.id,
        label: company.name,
      }));
  }, [companyList]);

  const hasSelectableCompanies = companyOptions.length > 0;

  // ユーザー一覧の取得
  const {
    data,
    refetch,
    isLoading,
    error: userListError,
  } = trpc.admin.getUsers.useQuery({
    page: 1,
    limit: 100,
    search: searchText || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    includeDeleted: showDeleted,
  });

  useEffect(() => {
    if (!companyListError) {
      return;
    }
    const errorMessage = companyListError instanceof Error
      ? companyListError.message
      : '会社一覧の取得に失敗しました';
    message.error(errorMessage);
  }, [companyListError, message]);

  useEffect(() => {
    if (!userListError) {
      return;
    }
    const errorMessage = userListError instanceof Error
      ? userListError.message
      : 'ユーザー一覧の取得に失敗しました';
    message.error(errorMessage);
  }, [userListError, message]);

  useEffect(() => {
    if (data?.users) {
      setUsers(
        data.users.flatMap((user) => {
          const normalized = normalizeUser(user);
          return normalized ? [normalized] : [];
        })
      );
    }
  }, [data]);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  const fetchUsers = () => {
    refetch();
  };

  // ユーザー作成mutation
  const createUserMutation = trpc.admin.createUser.useMutation({
    onSuccess: () => {
      message.success('ユーザーを作成しました');
      setIsCreateModalVisible(false);
      createForm.resetFields();
      refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'ユーザーの作成に失敗しました';
      message.error(errorMessage);
    },
  });

  // ユーザー作成
  const handleCreate = (values: CreateUserFormValues) => {
    if (!values.companyId) {
      message.error('会社を選択してください');
      return;
    }
    createUserMutation.mutate({
      ...values,
      isActive: values.isActive ?? true,
    });
  };

  // ユーザー更新mutation
  const updateUserMutation = trpc.admin.updateUser.useMutation({
    onSuccess: () => {
      message.success('ユーザー情報を更新しました');
      setIsEditModalVisible(false);
      editForm.resetFields();
      setEditingUser(null);
      refetch();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'ユーザー情報の更新に失敗しました';
      message.error(errorMessage);
    },
  });

  // ユーザー更新
  const handleUpdate = (values: UpdateUserFormValues) => {
    if (!editingUser) return;
    updateUserMutation.mutate({
      id: editingUser.id,
      ...values,
    });
  };

  // パスワードリセットmutation
  const resetPasswordMutation = trpc.admin.resetPassword.useMutation({
    onSuccess: () => {
      message.success('パスワードをリセットしました');
      setIsPasswordModalVisible(false);
      passwordForm.resetFields();
      setEditingUser(null);
    },
    onError: () => {
      message.error('パスワードのリセットに失敗しました');
    },
  });

  // パスワードリセット
  const handlePasswordReset = (values: ResetPasswordFormValues) => {
    if (!editingUser) return;
    resetPasswordMutation.mutate({
      userId: editingUser.id,
      newPassword: values.newPassword,
    });
  };


  const columns = [
    {
      title: '名前',
      dataIndex: 'name',
      key: 'name',
      render: (_text: string, record: User) => (
        <Space>
          <Link to={adminPath(`users/${record.id}`)} style={{ fontWeight: 600 }}>
            {record.name}
          </Link>
          {record.deletedAt && <Tag color="volcano">削除済み</Tag>}
          {!record.deletedAt && !record.isActive && <Tag color="red">停止</Tag>}
        </Space>
      ),
    },
    {
      title: '会社',
      dataIndex: 'companyName',
      key: 'company',
      render: (_value: string | null, record: User) =>
        record.companyName ? (
          <Space size={4}>
            <Text>{record.companyName}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'ロール',
      dataIndex: 'role',
      key: 'role',
      render: (role: UserRole) => (
        <Tag color={roleColors[role] || 'default'}>
          {roleLabels[role] || role}
        </Tag>
      ),
    },
    {
      title: 'ステータス',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_value: boolean, record: User) =>
        record.deletedAt ? (
          <Tag color="volcano">削除済み</Tag>
        ) : (
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? '稼働中' : '停止'}
          </Tag>
        ),
    },
    {
      title: '最終ログイン',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString('ja-JP') : '-',
    },
    {
      title: '作成日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ja-JP'),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: User) => {
        const additionalActions: MenuProps['items'] = [
          {
            key: 'password',
            icon: <KeyOutlined />,
            label: 'パスワードリセット',
            onClick: () => {
              setEditingUser(record);
              setIsPasswordModalVisible(true);
            },
            disabled: !!record.deletedAt,
          },
        ];

        return (
          <ListActionsDropdown
            onEdit={() => {
              setEditingUser(record);
              editForm.setFieldsValue({
                name: record.name,
                email: record.email,
                isActive: record.isActive,
                role: record.role,
              });
              setIsEditModalVisible(true);
            }}
            additionalActions={additionalActions}
          />
        );
      },
    },
  ];


  return (
    <PageContainer>
      <PageHeader
        title="ユーザー管理"
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
          >
            新規ユーザー作成
          </Button>
        }
      />

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'flex-start' }}>
          <Space size={12} wrap>
            <Input
              placeholder="名前・メールアドレスで検索"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
            <Select
              value={statusFilter}
              style={{ width: 150 }}
              onChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
            >
              <Option value="all">すべて</Option>
              <Option value="active">稼働中</Option>
              <Option value="inactive">停止</Option>
            </Select>
            <Space align="center">
              <Switch
                checked={showDeleted}
                onChange={setShowDeleted}
              />
              <Text type="secondary">削除済みを表示</Text>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
              更新
            </Button>
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `全 ${total} 件`,
          }}
        />
      </Card>

      {/* ユーザー作成モーダル */}
      <Modal
        title="新規ユーザー作成"
        open={isCreateModalVisible}
        confirmLoading={getMutationPendingState(createUserMutation)}
        onOk={() => createForm.submit()}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        okText="作成"
        cancelText="キャンセル"
        okButtonProps={{
          disabled: !hasSelectableCompanies || companyLoading,
        }}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="companyId"
            label="会社"
            rules={[{ required: true, message: '会社を選択してください' }]}
            extra={
              !companyLoading && !hasSelectableCompanies
                ? '会社が登録されていません。先に会社管理で会社を作成してください。'
                : undefined
            }
          >
            <Select
              placeholder="会社を選択"
              loading={companyLoading}
              options={companyOptions}
              disabled={!hasSelectableCompanies}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                typeof option?.label === 'string'
                  ? option.label.toLowerCase().includes(input.toLowerCase())
                  : false
              }
              data-testid="admin-user-company-select"
              notFoundContent={companyLoading ? '読み込み中...' : '会社が登録されていません'}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="名前"
            rules={[{ required: true, message: '名前を入力してください' }]}
          >
            <Input placeholder="田中 太郎" />
          </Form.Item>
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <Input placeholder="tanaka@avs1866.com" />
          </Form.Item>
          <Form.Item
            name="password"
            label="パスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上である必要があります' },
            ]}
          >
            <Input.Password placeholder="8文字以上のパスワード" />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="アクティブ"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ユーザー編集モーダル */}
      <Modal
        title="ユーザー情報編集"
        open={isEditModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
          setEditingUser(null);
        }}
        confirmLoading={getMutationPendingState(updateUserMutation)}
        okText="更新"
        cancelText="キャンセル"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="name"
            label="名前"
            rules={[{ required: true, message: '名前を入力してください' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="アクティブ"
            valuePropName="checked"
          >
            <Switch checkedChildren="有効" unCheckedChildren="無効" />
          </Form.Item>
          <Form.Item
            name="role"
            label="ロール"
            rules={[{ required: true, message: 'ロールを選択してください' }]}
          >
            <Select placeholder="ロールを選択">
              <Option value="admin">管理者</Option>
              <Option value="manager">マネージャー</Option>
              <Option value="user">ユーザー</Option>
              <Option value="viewer">閲覧者</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* パスワードリセットモーダル */}
      <Modal
        title="パスワードリセット"
        open={isPasswordModalVisible}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          passwordForm.resetFields();
          setEditingUser(null);
        }}
        confirmLoading={getMutationPendingState(resetPasswordMutation)}
        okText="リセット"
        cancelText="キャンセル"
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordReset}
        >
          {editingUser && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                ユーザー: {editingUser.name} ({editingUser.email})
              </Text>
            </div>
          )}
          <Form.Item
            name="newPassword"
            label="新しいパスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上である必要があります' },
            ]}
          >
            <Input.Password placeholder="8文字以上のパスワード" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="パスワード確認"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'パスワードを再入力してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="パスワードを再入力" />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
}

